/**
 * Firework effect recipes — real shell types mapped to particle behaviors.
 * Used by the summer weather-machine volley / finale system.
 */

export const FIREWORK_EFFECTS = {
  peony: {
    id: 'peony',
    name: 'Peony',
    weight: 28,
    finaleWeight: 8,
    colors: null, // use theme colors
    sparkCount: [28, 48],
    speed: [1.2, 3.2],
    drag: 0.995,
    sparkGravity: 0.06,
    decay: [0.014, 0.028],
    size: [4, 7],
    trail: false,
    trailLength: 0,
    blink: false,
    glitter: false,
    splitAfter: null,
    spawnShape: 'sphere',
    secondaryBurst: false,
    risingTail: false,
  },
  chrysanthemum: {
    id: 'chrysanthemum',
    name: 'Chrysanthemum',
    weight: 22,
    finaleWeight: 12,
    colors: null,
    sparkCount: [30, 52],
    speed: [1.2, 3.0],
    drag: 0.992,
    sparkGravity: 0.055,
    decay: [0.012, 0.024],
    size: [4, 7],
    trail: true,
    trailLength: 6,
    blink: false,
    glitter: false,
    splitAfter: null,
    spawnShape: 'sphere',
    secondaryBurst: false,
    risingTail: false,
  },
  willow: {
    id: 'willow',
    name: 'Willow',
    weight: 10,
    finaleWeight: 16,
    colors: ['#ffe082', '#ffd54f', '#ffecb3', '#fff8e1', '#e0e0e0', '#bdbdbd'],
    sparkCount: [36, 60],
    speed: [0.9, 2.2],
    drag: 0.96,
    sparkGravity: 0.085,
    decay: [0.006, 0.012],
    size: [3, 6],
    trail: true,
    trailLength: 12,
    blink: false,
    glitter: false,
    splitAfter: null,
    spawnShape: 'sphere',
    secondaryBurst: false,
    risingTail: false,
  },
  strobe: {
    id: 'strobe',
    name: 'Strobe',
    weight: 10,
    finaleWeight: 18,
    colors: ['#ffffff', '#e3f2fd', '#fff9c4', '#c8e6c9', '#ffccbc'],
    sparkCount: [24, 40],
    speed: [1.0, 2.6],
    drag: 0.99,
    sparkGravity: 0.07,
    decay: [0.008, 0.016],
    size: [4, 8],
    trail: false,
    trailLength: 0,
    blink: true,
    blinkPeriod: [4, 9],
    glitter: false,
    splitAfter: null,
    spawnShape: 'sphere',
    secondaryBurst: false,
    risingTail: false,
  },
  glitter: {
    id: 'glitter',
    name: 'Glitter',
    weight: 10,
    finaleWeight: 16,
    colors: ['#ffeb3b', '#fff59d', '#ffe082', '#ff9800', '#ffffff'],
    sparkCount: [22, 38],
    speed: [1.1, 2.8],
    drag: 0.988,
    sparkGravity: 0.065,
    decay: [0.01, 0.02],
    size: [4, 7],
    trail: true,
    trailLength: 4,
    blink: false,
    glitter: true,
    splitAfter: null,
    spawnShape: 'sphere',
    secondaryBurst: false,
    risingTail: false,
  },
  crossette: {
    id: 'crossette',
    name: 'Crossette (X)',
    weight: 10,
    finaleWeight: 14,
    colors: null,
    // Primaries stay modest; each splits into a hard 4-tip X
    sparkCount: [8, 12],
    speed: [1.6, 3.4],
    drag: 0.99,
    sparkGravity: 0.05,
    decay: [0.01, 0.02],
    size: [5, 8],
    trail: true,
    trailLength: 5,
    blink: false,
    glitter: false,
    splitAfter: [12, 22],
    spawnShape: 'sphere',
    secondaryBurst: false,
    risingTail: false,
  },
  ring: {
    id: 'ring',
    name: 'Ring',
    weight: 8,
    finaleWeight: 10,
    colors: null,
    sparkCount: [24, 36],
    speed: [1.4, 2.6],
    drag: 0.994,
    sparkGravity: 0.055,
    decay: [0.012, 0.022],
    size: [4, 7],
    trail: false,
    trailLength: 0,
    blink: false,
    glitter: false,
    splitAfter: null,
    spawnShape: 'ring',
    secondaryBurst: false,
    risingTail: false,
  },
  palm: {
    id: 'palm',
    name: 'Palm',
    weight: 5,
    finaleWeight: 12,
    colors: ['#ffd54f', '#ffb300', '#ffe082', '#ff8f00', '#fff8e1'],
    sparkCount: [5, 8],
    speed: [1.8, 3.6],
    drag: 0.985,
    sparkGravity: 0.08,
    decay: [0.008, 0.015],
    size: [6, 10],
    trail: true,
    trailLength: 14,
    blink: false,
    glitter: false,
    splitAfter: null,
    spawnShape: 'palm',
    secondaryBurst: false,
    risingTail: true,
  },
  colorChange: {
    id: 'colorChange',
    name: 'Color Change',
    weight: 8,
    finaleWeight: 12,
    colors: ['#ff5252', '#40c4ff'],
    colorsB: ['#69f0ae', '#e040fb', '#ffeb3b', '#ffffff'],
    sparkCount: [26, 44],
    speed: [1.2, 3.0],
    drag: 0.993,
    sparkGravity: 0.06,
    decay: [0.011, 0.02],
    size: [4, 7],
    trail: false,
    trailLength: 0,
    blink: false,
    glitter: false,
    splitAfter: null,
    spawnShape: 'sphere',
    secondaryBurst: false,
    risingTail: false,
    colorSwapAt: 0.55, // life threshold
  },
  pistil: {
    id: 'pistil',
    name: 'Pistil',
    weight: 7,
    finaleWeight: 14,
    colors: null,
    centerColors: ['#ffffff', '#fff9c4', '#ffecb3'],
    sparkCount: [24, 40],
    centerCount: [10, 18],
    speed: [1.3, 3.2],
    centerSpeed: [0.2, 0.8],
    drag: 0.994,
    sparkGravity: 0.06,
    decay: [0.013, 0.024],
    size: [4, 7],
    trail: false,
    trailLength: 0,
    blink: false,
    glitter: false,
    splitAfter: null,
    spawnShape: 'sphere',
    secondaryBurst: false,
    risingTail: false,
  },
  /**
   * Kamuro (Japanese "boy's haircut"): dense gold/silver dome with
   * very long-lasting glitter trails that hang in the sky.
   */
  kamuro: {
    id: 'kamuro',
    name: 'Kamuro',
    weight: 8,
    finaleWeight: 16,
    colors: ['#ffe082', '#ffd54f', '#fff8e1', '#eceff1', '#b0bec5', '#ffecb3'],
    // Dense look without flooding the global spark cap (was stalling later bursts)
    sparkCount: [28, 44],
    speed: [0.7, 1.9],
    drag: 0.97,
    sparkGravity: 0.05,
    decay: [0.007, 0.013],
    size: [3, 6],
    trail: true,
    trailLength: 14,
    blink: false,
    glitter: true,
    glitterChance: 0.05, // crumbs less often than generic glitter shells
    splitAfter: null,
    spawnShape: 'sphere',
    secondaryBurst: false,
    risingTail: false,
  },
  /**
   * Horsetail / waterfall: soft pour — stars travel a short distance
   * then cascade downward in a long glittering curtain.
   */
  horsetail: {
    id: 'horsetail',
    name: 'Horsetail',
    weight: 8,
    finaleWeight: 14,
    colors: ['#ffe082', '#ffcc80', '#fff59d', '#e0e0e0', '#bdbdbd', '#ffecb3'],
    sparkCount: [26, 42],
    speed: [0.5, 1.4],
    drag: 0.97,
    sparkGravity: 0.11,
    decay: [0.007, 0.013],
    size: [3, 6],
    trail: true,
    trailLength: 12,
    blink: false,
    glitter: true,
    glitterChance: 0.06,
    splitAfter: null,
    spawnShape: 'horsetail',
    secondaryBurst: false,
    risingTail: false,
  },
};

function randRange(pair) {
  if (!pair || pair.length < 2) return pair?.[0] ?? 0;
  return pair[0] + Math.random() * (pair[1] - pair[0]);
}

function weightedPick(effects, useFinaleWeight) {
  let total = 0;
  const entries = effects.map((e) => {
    const w = useFinaleWeight ? (e.finaleWeight ?? e.weight) : e.weight;
    total += w;
    return { e, w };
  });
  let roll = Math.random() * total;
  for (const { e, w } of entries) {
    roll -= w;
    if (roll <= 0) return e;
  }
  return entries[entries.length - 1].e;
}

/**
 * @param {{ finale?: boolean, prefer?: string[], exclude?: string[] }} [opts]
 * @returns {typeof FIREWORK_EFFECTS[keyof typeof FIREWORK_EFFECTS]}
 */
export function pickFireworkEffect(opts = {}) {
  const { finale = false, prefer = null, exclude = [] } = opts;
  let list = Object.values(FIREWORK_EFFECTS).filter((e) => !exclude.includes(e.id));

  if (prefer?.length) {
    const preferred = list.filter((e) => prefer.includes(e.id));
    if (preferred.length) list = preferred;
  }

  return weightedPick(list, finale);
}

/** Mortar shell calibers — bigger = fatter break, not “leave the frame.” */
export const SHELL_CALIBERS = {
  three: {
    id: 'three',
    name: '3″',
    weight: 38,
    finaleWeight: 18,
    forceScale: 0.92,
    sparkScale: 0.72,
    sizeScale: 0.78,
    speedScale: 0.9,
    gravityScale: 1.04,
    // Prefer lower-mid sky so small shells don't feel lost
    apexBand: [0.28, 0.72],
    rocketW: 3,
    rocketH: 6,
    trailDot: 2,
  },
  five: {
    id: 'five',
    name: '5″',
    weight: 36,
    finaleWeight: 28,
    forceScale: 1.0,
    sparkScale: 1.0,
    sizeScale: 1.0,
    speedScale: 1.0,
    gravityScale: 1.0,
    apexBand: [0.22, 0.62],
    rocketW: 5,
    rocketH: 9,
    trailDot: 3,
  },
  eight: {
    id: 'eight',
    name: '8″',
    weight: 18,
    finaleWeight: 32,
    // Presence comes from spark/size scale — keep loft on-stage
    forceScale: 1.06,
    sparkScale: 1.45,
    sizeScale: 1.4,
    speedScale: 1.18,
    gravityScale: 0.94,
    apexBand: [0.18, 0.5],
    rocketW: 8,
    rocketH: 14,
    trailDot: 5,
  },
  twelve: {
    id: 'twelve',
    name: '12″',
    weight: 8,
    finaleWeight: 22,
    forceScale: 1.1,
    sparkScale: 1.85,
    sizeScale: 1.75,
    speedScale: 1.28,
    gravityScale: 0.9,
    // Break in the heart of the viewing box, never above the top edge
    apexBand: [0.2, 0.48],
    rocketW: 12,
    rocketH: 18,
    trailDot: 7,
  },
};

export function getShellCaliber(id) {
  return SHELL_CALIBERS[id] || SHELL_CALIBERS.five;
}

export function pickShellCaliber(opts = {}) {
  const { finale = false, prefer = null } = opts;
  let list = Object.values(SHELL_CALIBERS);
  if (prefer?.length) {
    const preferred = list.filter((c) => prefer.includes(c.id));
    if (preferred.length) list = preferred;
  }
  return weightedPick(list, finale);
}

/**
 * Pick a volley choreography setup (effect mixing within a volley).
 * Kept for callers; prefer pickVolleyDesign for intentional shows.
 * @param {{ finale?: boolean }} [opts]
 * @returns {'mixed'|'uniform'|'pair'}
 */
export function pickVolleySetup(opts = {}) {
  const r = Math.random();
  if (opts.finale) {
    if (r < 0.45) return 'uniform';
    if (r < 0.8) return 'pair';
    return 'mixed';
  }
  if (r < 0.55) return 'mixed';
  if (r < 0.82) return 'uniform';
  return 'pair';
}

/** Curated color stories — pairs/triads that read well together in the sky */
export const VOLLEY_COLOR_STORIES = {
  rubyGold: ['#ff5252', '#ffd54f'],
  iceEmber: ['#40c4ff', '#ff6e40'],
  garden: ['#69f0ae', '#e040fb'],
  sunrise: ['#ff9800', '#fff59d', '#ff5252'],
  moonlight: ['#eceff1', '#90caf9', '#ffe082'],
  candy: ['#ff4081', '#7c4dff', '#40c4ff'],
  forest: ['#a5d6a7', '#ffcc80', '#ffffff'],
};

/**
 * Intentional volley designs — not "always one color", but *chosen* composition.
 * Each picks a color story, height staging, and timing that fit a spatial pattern.
 */
export const VOLLEY_DESIGNS = {
  monoStatement: {
    id: 'monoStatement',
    name: 'Mono Statement',
    weight: 14,
    finaleWeight: 10,
    patterns: ['fan', 'frontage', 'zee', 'single', 'bouquet'],
    colorMode: 'mono',
    heightMode: 'flat',
    timingMode: 'tight',
    effectMode: 'mono',
  },
  complementCross: {
    id: 'complementCross',
    name: 'Complement Cross',
    weight: 20,
    finaleWeight: 18,
    patterns: ['cross'], // always the X, not watered-down converge
    colorMode: 'duoAlt', // alternate A/B by shot index
    heightMode: 'flat',
    timingMode: 'tight',
    effectMode: 'mono',
  },
  leftRightTalk: {
    id: 'leftRightTalk',
    name: 'Left-Right Talk',
    weight: 14,
    finaleWeight: 12,
    patterns: ['converse', 'chase', 'zipper'],
    colorMode: 'duoSide', // left pad A, right pad B
    heightMode: 'flat',
    timingMode: 'rippled',
    effectMode: 'duo', // two related effects
  },
  stairway: {
    id: 'stairway',
    name: 'Stairway',
    weight: 12,
    finaleWeight: 14,
    patterns: ['chase', 'frontage', 'fan', 'cascade'],
    colorMode: 'triad',
    heightMode: 'stair', // each later shell a bit loftier
    timingMode: 'rippled',
    effectMode: 'mono',
  },
  leadAndCrown: {
    id: 'leadAndCrown',
    name: 'Lead & Crown',
    weight: 14,
    finaleWeight: 16,
    patterns: ['frontage', 'centerFocus', 'fan', 'cross'],
    colorMode: 'duoSplit', // first wave color A, crown color B
    heightMode: 'leadCrown', // majority lower, last 2 higher/faster
    timingMode: 'twoWaves',
    effectMode: 'leadCrown', // peony-ish then willow/pistil
  },
  gardenMix: {
    id: 'gardenMix',
    name: 'Garden Mix',
    weight: 12,
    finaleWeight: 10,
    patterns: ['frontage', 'fan', 'chase', 'single', 'bouquet', 'cascade'],
    colorMode: 'storyMix',
    heightMode: 'varied',
    timingMode: 'tight',
    effectMode: 'mixedFamily',
  },
  mirroredPair: {
    id: 'mirroredPair',
    name: 'Mirrored Pair',
    weight: 12,
    finaleWeight: 14,
    patterns: ['cross', 'converse', 'zee', 'wings'],
    colorMode: 'mono',
    heightMode: 'flat',
    timingMode: 'tight',
    effectMode: 'mono',
  },
  punchline: {
    id: 'punchline',
    name: 'Punchline',
    weight: 10,
    finaleWeight: 18,
    patterns: ['centerFocus', 'zee', 'frontage'],
    colorMode: 'duoSplit',
    heightMode: 'leadCrown',
    timingMode: 'twoWaves',
    effectMode: 'leadCrown',
  },
};

export function pickVolleyDesign(opts = {}) {
  return weightedPick(Object.values(VOLLEY_DESIGNS), !!opts.finale);
}

function pickColorStory() {
  const keys = Object.keys(VOLLEY_COLOR_STORIES);
  return VOLLEY_COLOR_STORIES[keys[Math.floor(Math.random() * keys.length)]];
}

/**
 * Resolve per-shot creative charges for a planned volley.
 * @returns {Array<{effectId, color, colorB, forceScale, delayBoost, gravity}>}
 */
export function resolveVolleyCharges(design, planned, opts = {}) {
  const finale = !!opts.finale;
  const story = pickColorStory();
  const colorA = story[0];
  const colorB = story[1] || story[0];
  const colorC = story[2] || colorA;

  const leadEffects = ['peony', 'chrysanthemum', 'ring', 'strobe'];
  const crownEffects = [
    'willow',
    'pistil',
    'palm',
    'glitter',
    'colorChange',
    'kamuro',
    'horsetail',
  ];
  const family = [
    'peony',
    'chrysanthemum',
    'willow',
    'strobe',
    'glitter',
    'ring',
    'palm',
    'pistil',
    'kamuro',
    'horsetail',
    'crossette',
    'colorChange',
  ];
  const exclude = [
    ...(opts.excludeEffects || []),
    ...(opts.mobile ? ['kamuro'] : []),
  ];
  const filterFx = (list) => list.filter((id) => !exclude.includes(id));

  const effectA = pickFireworkEffect({
    finale,
    prefer: filterFx(
      design.effectMode === 'leadCrown' ? leadEffects : family
    ),
    exclude,
  });
  const effectB = pickFireworkEffect({
    finale,
    prefer: filterFx(
      design.effectMode === 'leadCrown'
        ? crownEffects
        : design.effectMode === 'duo'
          ? crownEffects
          : family
    ),
    exclude: design.effectMode === 'duo' ? [...exclude, effectA.id] : exclude,
  });

  const n = planned.length;
  const crownCount = design.heightMode === 'leadCrown'
    ? Math.min(2, Math.max(1, Math.floor(n * 0.35)))
    : 0;
  const leadCount = n - crownCount;

  const charges = [];
  let matchedCaliberId = null;
  for (let i = 0; i < n; i++) {
    const shot = planned[i];
    const inCrown = design.heightMode === 'leadCrown' && i >= leadCount;
    const t = n <= 1 ? 0.5 : i / (n - 1);

    let effectId = effectA.id;
    let color = colorA;
    let cB = colorB;
    let forceScale = 1;
    let delayBoost = 0;
    let gravity = 0.11 + Math.random() * 0.02;

    // --- color modes ---
    if (design.colorMode === 'mono') {
      color = colorA;
      cB = colorA;
    } else if (design.colorMode === 'duoAlt') {
      color = i % 2 === 0 ? colorA : colorB;
      cB = color;
    } else if (design.colorMode === 'duoSide') {
      color = shot.site?.role === 'right' ? colorB : colorA;
      cB = color;
    } else if (design.colorMode === 'duoSplit') {
      color = inCrown ? colorB : colorA;
      cB = inCrown ? colorA : colorB;
    } else if (design.colorMode === 'triad') {
      color = [colorA, colorB, colorC][i % 3];
      cB = color;
    } else if (design.colorMode === 'storyMix') {
      color = story[i % story.length];
      cB = story[(i + 1) % story.length];
    }

    // --- effect modes ---
    if (design.effectMode === 'mono' || design.effectMode === undefined) {
      effectId = effectA.id;
    } else if (design.effectMode === 'duo') {
      effectId = i % 2 === 0 ? effectA.id : effectB.id;
    } else if (design.effectMode === 'leadCrown') {
      effectId = inCrown ? effectB.id : effectA.id;
    } else if (design.effectMode === 'mixedFamily') {
      effectId = pickFireworkEffect({
        finale,
        prefer: filterFx(family),
        exclude,
      }).id;
    }

    // --- height staging ---
    if (design.heightMode === 'flat') {
      forceScale = 1;
    } else if (design.heightMode === 'stair') {
      forceScale = 0.82 + t * 0.4;
    } else if (design.heightMode === 'leadCrown') {
      // Crown = fatter shells on-stage, not much higher
      forceScale = inCrown ? 1.08 : 0.92 + Math.random() * 0.06;
    } else if (design.heightMode === 'varied') {
      forceScale = 0.85 + Math.random() * 0.35;
    }

    // --- timing ---
    if (design.timingMode === 'rippled') {
      delayBoost = i * (45 + Math.random() * 25);
    } else if (design.timingMode === 'twoWaves') {
      delayBoost = inCrown ? 280 + (i - leadCount) * 60 : i * 35;
    } else if (design.timingMode === 'tight') {
      delayBoost = i * (18 + Math.random() * 12);
    }

    // Slight gravity trim on crown shells so they hang a touch longer
    if (inCrown) gravity = 0.095 + Math.random() * 0.02;

    // --- caliber staging ---
    let caliberId;
    const mobileCalibers = opts.mobile;
    if (design.heightMode === 'leadCrown') {
      caliberId = inCrown
        ? pickShellCaliber({
            finale,
            prefer: mobileCalibers ? ['five', 'eight'] : ['eight', 'twelve'],
          }).id
        : pickShellCaliber({ finale, prefer: ['three', 'five'] }).id;
    } else if (design.heightMode === 'stair') {
      if (t < 0.34) caliberId = 'three';
      else if (t < 0.67) caliberId = 'five';
      else
        caliberId = pickShellCaliber({
          finale,
          prefer: mobileCalibers ? ['five', 'eight'] : ['eight', 'twelve'],
        }).id;
    } else if (design.heightMode === 'varied') {
      caliberId = pickShellCaliber({
        finale,
        prefer: mobileCalibers ? ['three', 'five', 'eight'] : null,
      }).id;
    } else {
      if (!matchedCaliberId) {
        matchedCaliberId = pickShellCaliber({
          finale,
          prefer: mobileCalibers
            ? ['three', 'five']
            : finale
              ? ['five', 'eight', 'twelve']
              : ['three', 'five', 'eight'],
        }).id;
      }
      caliberId = matchedCaliberId;
    }

    const caliber = getShellCaliber(caliberId);
    forceScale *= caliber.forceScale;
    gravity *= caliber.gravityScale;

    charges.push({
      effectId,
      color,
      colorB: cB,
      forceScale,
      delayBoost,
      gravity,
      caliberId,
      wave: inCrown ? 'crown' : 'lead',
    });
  }

  return { charges, story, design };
}

/**
 * Spatial volley patterns inspired by real pyromusical frontage:
 * left / center / right positions, angled mortars, crossing flight paths.
 *
 * - single: one pad, slight random lean
 * - fan: one pad, spread of aiming angles (mortar fan rack)
 * - cross: L leans in/right, R leans in/left so shells cross mid-sky
 * - converse: L↔R conversation (alternating sides)
 * - frontage: all positions fire together (wall of sky)
 * - centerFocus: center gets lofty "statement" shells; sides fill with lower fans
 * - chase: L → C → R wave (or reverse)
 * - zee: L and R aim toward center peak (converging breaks)
 * - bouquet: dense near-simultaneous cluster into one break pocket
 * - cascade: waterfall-style L→R sweep with stepping-down altitude
 * - wings: mirrored L/R pairs firing together (symmetrical flanks)
 * - zipper: rapid L/R alternate walking every pad in the batteries
 */
export const VOLLEY_PATTERNS = {
  single: { id: 'single', name: 'Single', weight: 12, finaleWeight: 4 },
  fan: { id: 'fan', name: 'Fan', weight: 16, finaleWeight: 10 },
  cross: { id: 'cross', name: 'Cross', weight: 18, finaleWeight: 18 },
  converse: { id: 'converse', name: 'Converse', weight: 14, finaleWeight: 12 },
  frontage: { id: 'frontage', name: 'Frontage', weight: 12, finaleWeight: 18 },
  centerFocus: { id: 'centerFocus', name: 'Center Focus', weight: 12, finaleWeight: 14 },
  chase: { id: 'chase', name: 'Chase', weight: 10, finaleWeight: 12 },
  zee: { id: 'zee', name: 'Converge', weight: 10, finaleWeight: 14 },
  bouquet: { id: 'bouquet', name: 'Bouquet', weight: 12, finaleWeight: 14 },
  cascade: { id: 'cascade', name: 'Cascade', weight: 11, finaleWeight: 12 },
  wings: { id: 'wings', name: 'Wings', weight: 13, finaleWeight: 15 },
  zipper: { id: 'zipper', name: 'Zipper', weight: 11, finaleWeight: 12 },
};

export function pickVolleyPattern(opts = {}) {
  const list = Object.values(VOLLEY_PATTERNS);
  return weightedPick(list, !!opts.finale);
}

/** Preferred break / viewing rectangle (audience sightline). */
export function getFireworkViewingZone(width, height, opts = {}) {
  if (opts.mobile) {
    // Taller mobile canvas: use more of the width, keep breaks mid-sky
    return {
      x0: width * 0.06,
      x1: width * 0.94,
      y0: height * 0.14,
      y1: height * 0.4,
      cx: width * 0.5,
      cy: height * 0.26,
    };
  }
  return {
    x0: width * 0.2,
    x1: width * 0.8,
    y0: height * 0.1,
    y1: height * 0.45,
    cx: width * 0.5,
    cy: height * 0.28,
  };
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Aim a mortar at a point inside the viewing zone so breaks stay on-stage.
 * Optional caliber biases break height into that shell's preferred apex band.
 * @returns {{ angle: number, force: number, targetX: number, targetY: number }}
 */
export function aimIntoViewingZone(site, zone, opts = {}) {
  const margin = 0.12;
  const spanX = zone.x1 - zone.x0;
  const spanY = zone.y1 - zone.y0;
  const caliber = opts.caliberId ? getShellCaliber(opts.caliberId) : null;
  const band = caliber?.apexBand || [0.15, 0.7];

  // Bias targets: side pads prefer the near-center half of the zone
  let tMin = margin;
  let tMax = 1 - margin;
  if (site.role === 'left') {
    tMin = 0.25;
    tMax = 0.72;
  } else if (site.role === 'right') {
    tMin = 0.28;
    tMax = 0.75;
  }

  const tx =
    opts.targetX != null
      ? opts.targetX
      : zone.x0 + spanX * (tMin + Math.random() * (tMax - tMin));

  // Prefer altitude inside the caliber's apex band (screen Y down)
  const ty =
    opts.targetY != null
      ? opts.targetY
      : zone.y0 + spanY * (band[0] + Math.random() * (band[1] - band[0]));

  const dx = tx - site.x;
  const dy = ty - site.y; // usually negative (up)
  let angle = Math.atan2(dy, dx === 0 ? 0.0001 : dx);

  // Keep loft mostly skyward; allow gentle cross without skipping out
  angle = clamp(angle, -Math.PI + 0.7, -0.7);

  // Soften extreme side flies: if angled too sideways relative to pad, pull toward UP
  const UP = -Math.PI / 2;
  const maxLean =
    opts.maxLean != null
      ? opts.maxLean
      : site.role === 'center'
        ? 0.38
        : 0.48;
  angle = clamp(angle, UP - maxLean, UP + maxLean);

  const climb = Math.max(40, site.y - ty);
  const lateral = Math.abs(dx);
  let force = 5.2 + climb / 95 + lateral / 220 + Math.random() * 1.2;
  force = clamp(force, 5, 11.5);

  // Final safety: keep ballistic apex inside the viewing box for this caliber
  force = fitForceToViewingZone(
    site,
    angle,
    force,
    opts.gravity != null ? opts.gravity : 0.12,
    zone,
    caliber
  );

  return { angle, force, targetX: tx, targetY: ty };
}

/**
 * Clamp launch force so the estimated apex lands in the viewing zone
 * (and inside the caliber's preferred apex band when provided).
 */
export function fitForceToViewingZone(site, angle, force, gravity, zone, caliber = null) {
  if (!site || !zone) return force;
  const s = Math.sin(angle);
  // Need an upward component
  if (s >= -0.08) return clamp(force, 4.5, 12);

  const band = caliber?.apexBand || [0.12, 0.7];
  const spanY = zone.y1 - zone.y0;
  // Hard floor: never break above the viewing-zone top (plus a tiny margin)
  const highest = Math.max(zone.y0 + 8, zone.y0 + spanY * band[0]);
  const lowest = Math.min(zone.y1 - 8, zone.y0 + spanY * band[1]);
  const g = Math.max(0.06, gravity || 0.12);
  const s2 = s * s;

  // apexY ≈ site.y - (sinθ · F)² / (2g)
  const forceForApex = (apexY) => {
    const rise = Math.max(20, site.y - apexY);
    return Math.sqrt((rise * 2 * g) / s2);
  };

  const fLo = forceForApex(lowest); // weaker → lower break
  const fHi = forceForApex(highest); // stronger → higher break
  return clamp(force, fLo, fHi);
}

/**
 * Group launch pads by battery role (left / center / right).
 * Each role may contain multiple pads (default 3 each = 9 total).
 */
export function groupSitesByRole(sites) {
  const left = sites.filter((s) => s.role === 'left');
  const center = sites.filter((s) => s.role === 'center');
  const right = sites.filter((s) => s.role === 'right');
  return {
    left: left.length ? left : sites.slice(0, 1),
    center: center.length ? center : [sites[Math.floor(sites.length / 2)]],
    right: right.length ? right : sites.slice(-1),
  };
}

function cycleSite(group, i) {
  return group[i % group.length];
}

/**
 * Build per-shell aim plan for a volley pattern.
 * Angles use canvas coords: -PI/2 = straight up.
 * Patterns rotate through pads inside each L/C/R battery so 9-site fields get used.
 *
 * @param {string} patternId
 * @param {number} count
 * @param {Array<{id:string, role:string, x:number, y:number, lean:number}>} sites
 * @param {{x0:number,x1:number,y0:number,y1:number,cx:number,cy:number}|null} [zone]
 * @returns {Array<{site: object, angle: number, force: number, delayBoost: number}>}
 */
export function planVolleyShots(patternId, count, sites, zone = null) {
  const n = Math.max(0, count);
  if (!n || !sites?.length) return [];

  const groups = groupSitesByRole(sites);
  const { left: leftPads, center: centerPads, right: rightPads } = groups;

  // Fallback zone from site spread if not provided
  const zs = zone || {
    x0: Math.min(...sites.map((s) => s.x)) - 40,
    x1: Math.max(...sites.map((s) => s.x)) + 40,
    y0: 40,
    y1: (sites[0]?.y || 400) * 0.45,
    cx: (Math.min(...sites.map((s) => s.x)) + Math.max(...sites.map((s) => s.x))) / 2,
    cy: (sites[0]?.y || 400) * 0.28,
  };

  const shots = [];

  const pushAimed = (site, delayBoost = 0, aimOpts = {}) => {
    const aim = aimIntoViewingZone(site, zs, aimOpts);
    shots.push({
      site,
      angle: aim.angle,
      force: aim.force,
      delayBoost,
      targetX: aim.targetX,
      targetY: aim.targetY,
    });
  };

  if (patternId === 'single' || sites.length === 1) {
    const site = sites[Math.floor(Math.random() * sites.length)];
    for (let i = 0; i < n; i++) {
      pushAimed(site, 0, {
        targetX: zs.cx + (Math.random() - 0.5) * (zs.x1 - zs.x0) * 0.35,
      });
    }
    return shots;
  }

  if (patternId === 'fan') {
    // Fan across the viewing zone, rotating through center-battery pads
    for (let i = 0; i < n; i++) {
      const site = cycleSite(centerPads, i);
      const t = n === 1 ? 0.5 : i / (n - 1);
      const tx = zs.x0 + (zs.x1 - zs.x0) * (0.15 + t * 0.7);
      const ty = zs.y0 + (zs.y1 - zs.y0) * (0.25 + Math.random() * 0.45);
      pushAimed(site, 0, { targetX: tx, targetY: ty });
    }
    return shots;
  }

  if (patternId === 'cross') {
    // True X: outer pads, deep into the opposite half, roomy lean so
    // viewing-zone soft clamps don't flatten the crossing arcs.
    const leftOuter = leftPads[0] || leftPads[0];
    const rightOuter = rightPads[rightPads.length - 1] || rightPads[0];
    for (let i = 0; i < n; i++) {
      const fromLeft = i % 2 === 0;
      // Prefer outer battery pipes; cycle inners on denser volleys
      const site = fromLeft
        ? cycleSite(
            [leftOuter, ...leftPads.filter((p) => p !== leftOuter)],
            Math.floor(i / 2)
          )
        : cycleSite(
            [rightOuter, ...rightPads.filter((p) => p !== rightOuter)],
            Math.floor(i / 2)
          );
      const tx = fromLeft
        ? zs.x0 + (zs.x1 - zs.x0) * (0.62 + Math.random() * 0.22)
        : zs.x0 + (zs.x1 - zs.x0) * (0.16 + Math.random() * 0.22);
      const ty = zs.y0 + (zs.y1 - zs.y0) * (0.25 + Math.random() * 0.4);
      pushAimed(site, (i % 2) * 35, {
        targetX: tx,
        targetY: ty,
        maxLean: 0.95,
      });
    }
    return shots;
  }

  if (patternId === 'converse') {
    for (let i = 0; i < n; i++) {
      const site =
        i % 2 === 0
          ? cycleSite(leftPads, Math.floor(i / 2))
          : cycleSite(rightPads, Math.floor(i / 2));
      const tx =
        zs.cx +
        (site.role === 'left' ? -1 : 1) *
          (zs.x1 - zs.x0) *
          (0.08 + Math.random() * 0.18);
      pushAimed(site, i * 55, { targetX: tx });
    }
    return shots;
  }

  if (patternId === 'frontage') {
    // Walk all pads (full 9-pipe wall when available)
    for (let i = 0; i < n; i++) {
      const site = sites[i % sites.length];
      let tx = zs.cx;
      if (site.role === 'left') {
        tx = zs.x0 + (zs.cx - zs.x0) * (0.35 + Math.random() * 0.45);
      } else if (site.role === 'right') {
        tx = zs.cx + (zs.x1 - zs.cx) * (0.2 + Math.random() * 0.45);
      } else {
        tx = zs.cx + (Math.random() - 0.5) * (zs.x1 - zs.x0) * 0.25;
      }
      pushAimed(site, Math.floor(i / sites.length) * 50, { targetX: tx });
    }
    return shots;
  }

  if (patternId === 'centerFocus') {
    const sideCount = Math.floor(n * 0.55);
    const centerCount = n - sideCount;
    for (let i = 0; i < sideCount; i++) {
      const site =
        i % 2 === 0
          ? cycleSite(leftPads, Math.floor(i / 2))
          : cycleSite(rightPads, Math.floor(i / 2));
      const tx =
        site.role === 'left'
          ? zs.x0 + (zs.cx - zs.x0) * (0.4 + Math.random() * 0.4)
          : zs.cx + (zs.x1 - zs.cx) * (0.2 + Math.random() * 0.4);
      const ty = zs.cy + (zs.y1 - zs.cy) * (0.3 + Math.random() * 0.5);
      pushAimed(site, 0, { targetX: tx, targetY: ty });
    }
    for (let i = 0; i < centerCount; i++) {
      pushAimed(cycleSite(centerPads, i), 30 + i * 40, {
        targetX: zs.cx + (Math.random() - 0.5) * (zs.x1 - zs.x0) * 0.2,
        targetY: zs.y0 + (zs.y1 - zs.y0) * (0.1 + Math.random() * 0.35),
      });
    }
    return shots;
  }

  if (patternId === 'chase') {
    // Sweep L→C→R (or reverse), advancing through pads within each battery
    const forward = Math.random() < 0.5;
    const batteryOrder = forward
      ? [leftPads, centerPads, rightPads]
      : [rightPads, centerPads, leftPads];
    for (let i = 0; i < n; i++) {
      const battery = batteryOrder[i % batteryOrder.length];
      const deep = Math.floor(i / batteryOrder.length);
      const site = cycleSite(battery, deep);
      pushAimed(site, (i % batteryOrder.length) * 70 + deep * 40, {
        targetX: zs.cx + (Math.random() - 0.5) * (zs.x1 - zs.x0) * 0.4,
      });
    }
    return shots;
  }

  if (patternId === 'zee') {
    // Reserve one shell for the center converge punchline when n ≥ 3
    const sideCount = n >= 3 ? n - 1 : n;
    for (let i = 0; i < sideCount; i++) {
      const fromLeft = i % 2 === 0;
      const site = fromLeft
        ? cycleSite(leftPads, Math.floor(i / 2))
        : cycleSite(rightPads, Math.floor(i / 2));
      pushAimed(site, (i % 2) * 35, {
        targetX: zs.cx + (Math.random() - 0.5) * (zs.x1 - zs.x0) * 0.15,
        targetY: zs.y0 + (zs.y1 - zs.y0) * (0.15 + Math.random() * 0.35),
      });
    }
    if (n >= 3) {
      pushAimed(cycleSite(centerPads, 0), 80, {
        targetX: zs.cx,
        targetY: zs.y0 + (zs.y1 - zs.y0) * 0.2,
      });
    }
    return shots;
  }

  if (patternId === 'bouquet') {
    // Classic shell bouquet: tight cluster into one pocket, near-simultaneous
    const pocketX = zs.cx + (Math.random() - 0.5) * (zs.x1 - zs.x0) * 0.22;
    const pocketY = zs.y0 + (zs.y1 - zs.y0) * (0.2 + Math.random() * 0.35);
    const battery =
      Math.random() < 0.45
        ? centerPads
        : Math.random() < 0.5
          ? leftPads
          : rightPads;
    for (let i = 0; i < n; i++) {
      pushAimed(cycleSite(battery, i), i * 18, {
        targetX: pocketX + (Math.random() - 0.5) * 28,
        targetY: pocketY + (Math.random() - 0.5) * 22,
      });
    }
    return shots;
  }

  if (patternId === 'cascade') {
    // Waterfall-inspired: walk L→R (or reverse) with stepping-down altitude
    const forward = Math.random() < 0.5;
    const ordered = forward
      ? [...leftPads, ...centerPads, ...rightPads]
      : [...rightPads, ...centerPads, ...leftPads];
    for (let i = 0; i < n; i++) {
      const site = cycleSite(ordered, i);
      const t = n === 1 ? 0 : i / (n - 1);
      const ty = zs.y0 + (zs.y1 - zs.y0) * (0.15 + t * 0.7);
      const tx =
        zs.x0 +
        (zs.x1 - zs.x0) *
          (forward ? 0.2 + t * 0.6 : 0.8 - t * 0.6) +
        (Math.random() - 0.5) * 18;
      pushAimed(site, i * 55, { targetX: tx, targetY: ty });
    }
    return shots;
  }

  if (patternId === 'wings') {
    // Symmetrical flanks: L and R fire as mirrored pairs
    const pairs = Math.ceil(n / 2);
    for (let i = 0; i < pairs; i++) {
      const t = pairs === 1 ? 0.5 : i / (pairs - 1);
      const spread = 0.18 + t * 0.28;
      const ty = zs.y0 + (zs.y1 - zs.y0) * (0.2 + Math.random() * 0.4);
      const leftSite = cycleSite(leftPads, i);
      const rightSite = cycleSite(rightPads, i);
      pushAimed(leftSite, i * 70, {
        targetX: zs.cx - (zs.x1 - zs.x0) * spread,
        targetY: ty,
        maxLean: 0.7,
      });
      if (shots.length < n) {
        pushAimed(rightSite, i * 70 + 12, {
          targetX: zs.cx + (zs.x1 - zs.x0) * spread,
          targetY: ty,
          maxLean: 0.7,
        });
      }
    }
    return shots.slice(0, n);
  }

  if (patternId === 'zipper') {
    // Rapid L/R zip across every pipe in the side batteries
    const zipPads = [];
    const maxSlots = Math.max(leftPads.length, rightPads.length);
    for (let s = 0; s < maxSlots; s++) {
      if (leftPads[s]) zipPads.push(leftPads[s]);
      if (rightPads[s]) zipPads.push(rightPads[s]);
    }
    if (!zipPads.length) zipPads.push(...sites);
    for (let i = 0; i < n; i++) {
      const site = zipPads[i % zipPads.length];
      const towardCenter =
        site.role === 'left'
          ? zs.cx - (zs.x1 - zs.x0) * 0.08
          : zs.cx + (zs.x1 - zs.x0) * 0.08;
      pushAimed(site, i * 38, {
        targetX: towardCenter + (Math.random() - 0.5) * 20,
        targetY: zs.y0 + (zs.y1 - zs.y0) * (0.25 + Math.random() * 0.4),
      });
    }
    return shots;
  }

  for (let i = 0; i < n; i++) {
    pushAimed(sites[i % sites.length]);
  }
  return shots;
}

export function getFireworkEffect(id) {
  return FIREWORK_EFFECTS[id] || FIREWORK_EFFECTS.peony;
}

/** Helpers exported for burst construction */
export const fireworkUtil = { randRange };
